/* Nolae Blog — per-line split for the section headings' entrance.
 *
 * Ported from the About Us prototype's public/2/hero-split.js. The measuring
 * and rebuilding below are that file's, unchanged; what differs is how groups
 * are chosen. About Us names a fixed list of selectors because it has one of
 * each heading; this page has five section heads, so each [data-split] element
 * is its own group and its lines count 0,1,2… from their own start.
 *
 * ORIGINAL HEADER FOLLOWS
 * ───────────────────────
 * Nolae About Us — per-line split for the hero's entrance.
 *
 * The reference (sendpotion.com) staggers its headline one LINE at a time, each
 * line swinging up out of a backward tilt. Lines are not addressable in CSS, so
 * they have to be made into elements — and where a line breaks depends on the
 * width and on which font is loaded, so it cannot be written into the markup
 * either. This measures the real breaks and wraps them, then re-measures whenever
 * they could have changed.
 *
 * Method: wrap every word in a span, read each one's top, group the spans that
 * share a top into one block per line — then throw the word spans away again,
 * collapsing each line back to plain text. That last step matters for screen
 * readers: a paragraph split into one element per word is read badly by some, and
 * once the lines are known the words have served their purpose. What is left is
 * three blocks of ordinary text.
 *
 * Non-text children survive as single tokens: the inline gif keeps its place
 * mid-sentence, and "K-pop" keeps the nowrap span that stops it breaking at its
 * hyphen.
 *
 * Four groups use this: the hero, which plays on load, and "Meet the team",
 * "Culture at Nolae" and "Content House of K-pop", which each hold until the
 * reader reaches them. The
 * splitting is identical either way — WHEN the lines animate is a CSS question,
 * keyed off a class the relevant section's own script adds.
 *
 * If this never runs, the CSS leaves the hero as plain static text — the entrance
 * is additive, never a precondition for reading the page.
 *
 * Deliberately framework-free (IIFE, no imports, no build step) so it drops into a
 * Shopify Liquid section unchanged:
 *   {{ 'blog-split.js' | asset_url | script_tag }}
 */
(function () {
  "use strict";

  /* Groups, not one flat list. Each group restarts the stagger index at 0, so
     the team's two blocks count 0,1,2... of their own rather than continuing
     from the hero's — sharing a counter would have handed the team's first line
     a delay several hundred ms long before it even started.
     Within a group the index DOES carry across elements, which is what makes
     the hero's eyebrow and headline read as one run of lines. */
  /* One group per section head. Inside a group the index carries across the
     heading and its blurb, so the two read as one run of lines — exactly as
     [".cult-title", ".cult-blurb"] does on About Us. */
  var GROUP_ROOT = "[data-split]";
  var IN_GROUP = ".row-title, .row-desc, .banner-title, .banner-desc";
  var lastWidth = -1;

  function tokenise(el) {
    // Words become spans; elements pass through whole.
    var tokens = [];
    var nodes = [].slice.call(el.childNodes);
    el.textContent = "";
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.nodeType === 3) {
        var parts = node.nodeValue.split(/(\s+)/);
        for (var j = 0; j < parts.length; j++) {
          if (!parts[j]) continue;
          if (/^\s+$/.test(parts[j])) {
            // Real whitespace, so the browser still has its break opportunities.
            el.appendChild(document.createTextNode(parts[j]));
          } else {
            var w = document.createElement("span");
            w.textContent = parts[j];
            el.appendChild(w);
            tokens.push(w);
          }
        }
      } else if (node.nodeType === 1) {
        el.appendChild(node);
        tokens.push(node);
      }
    }
    return tokens;
  }

  function split(el, startIndex) {
    /* A re-split (resize, font arrival) of a heading whose entrance has
       already played must not play it again: the rebuilt lines are new
       elements and would restart the keyframe from invisible. */
    var again = el.__orig != null && !!el.closest(".is-arrived");
    if (el.__orig == null) el.__orig = el.innerHTML;
    // Hidden across the rebuild so the un-split text is never painted; both
    // happen in one task, so there is no flash either way.
    el.style.visibility = "hidden";
    el.classList.remove("is-split");
    el.innerHTML = el.__orig;

    var tokens = tokenise(el);
    if (!tokens.length) { el.style.visibility = ""; return startIndex; }

    /* Group into lines by each token's vertical CENTRE, against half a line-height
       of tolerance.
       Not by rect top with a small tolerance, which is the obvious version and is
       wrong here: tokens on one line do not share a top. A word's box is its
       glyphs, the inline gif's is 75px tall and vertically centred, and the two
       differ by enough to look like separate lines — which split the sentence's
       middle line into three and stretched the headline from 234 to 390.
       Centres of tokens on one line agree closely, while the next line's are a
       full line-height away, so half a line-height separates them cleanly whatever
       is sitting in the text. */
    var lh = parseFloat(getComputedStyle(el).lineHeight);
    if (!lh || isNaN(lh)) lh = parseFloat(getComputedStyle(el).fontSize) * 1.3 || 20;
    var tol = lh * 0.5;

    var lines = [];
    var lineCentre = null;
    for (var i = 0; i < tokens.length; i++) {
      var box = tokens[i].getBoundingClientRect();
      /* A token that is not rendered has no position to group by, and its zero
         rect sits nowhere near the real lines — so it would start a line of its
         own. That is not hypothetical: the inline gif is display:none at the
         mobile breakpoint, where it split the middle of the sentence in three and
         left the headline a line taller than it should be. Carry such tokens along
         with whatever line is open instead. */
      if (!box.width && !box.height) {
        if (!lines.length) lines.push([]);
        lines[lines.length - 1].push(tokens[i]);
        continue;
      }
      var centre = box.top + box.height / 2;
      if (lineCentre === null || Math.abs(centre - lineCentre) > tol) {
        lines.push([]);
        lineCentre = centre;
      }
      lines[lines.length - 1].push(tokens[i]);
    }

    // Rebuild as one block per line, collapsing the word spans back to text.
    var frag = document.createDocumentFragment();
    for (var l = 0; l < lines.length; l++) {
      var line = document.createElement("span");
      line.className = "hero-line";
      line.style.setProperty("--i", String(startIndex + l));
      if (again) line.style.animation = "none";
      var group = lines[l];
      for (var t = 0; t < group.length; t++) {
        var tok = group[t];
        /* A <br> did its job during measurement — it is why this group is a
           separate line at all. Carrying it into the rebuilt line would add an
           empty line box below the text and inflate the block's height. */
        if (tok.tagName === "BR") continue;
        var isWord = tok.tagName === "SPAN" && !tok.className;
        if (isWord) {
          var prev = line.lastChild;
          var text = tok.textContent;
          // Put the separating space back between words on the same line.
          if (t > 0) text = " " + text;
          if (prev && prev.nodeType === 3) prev.nodeValue += text;
          else line.appendChild(document.createTextNode(text));
        } else {
          if (t > 0) line.appendChild(document.createTextNode(" "));
          line.appendChild(tok);
        }
      }
      frag.appendChild(line);
    }
    el.textContent = "";
    el.appendChild(frag);

    el.style.visibility = "";
    el.classList.add("is-split");
    return startIndex + lines.length;
  }

  /* `force` re-splits groups already done (line breaks may have moved);
     without it only groups not yet split are touched — which is what lets
     this run twice: once mid-parse for the category rows, and again at
     DOMContentLoaded for the "All articles" head that arrives after them.
     See the note at the top of blog-reveal.js for why the first pass exists. */
  function run(force) {
    var groups = document.querySelectorAll(GROUP_ROOT);
    for (var g = 0; g < groups.length; g++) {
      if (groups[g].__split && !force) continue;
      groups[g].__split = true;
      var index = 0;
      var els = groups[g].querySelectorAll(IN_GROUP);
      for (var i = 0; i < els.length; i++) index = split(els[i], index);
    }
    lastWidth = window.innerWidth;
  }

  function onResize() {
    // Only width can change where a line breaks; height cannot, and on mobile it
    // changes constantly as the address bar comes and goes.
    if (window.innerWidth === lastWidth) return;
    run(true);
  }

  var fontsLoaded = !!(document.fonts && document.fonts.status === "loaded");

  function init() {
    run();
    window.addEventListener("resize", onResize);
    // The fallback face breaks lines somewhere else, so re-split once Omnes
    // lands — unless it already had when the first split was measured.
    if (document.fonts && document.fonts.ready && !fontsLoaded) {
      document.fonts.ready.then(function () { run(true); });
    }
  }

  // First pass right now, mid-parse, for whatever heads already exist; the
  // second at DOMContentLoaded for the rest.
  init();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { run(); });
  }
})();
